import React, { useState } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Stack from '@mui/material/Stack';
import { Icon } from '@iconify/react';
import { Box, Grid, Typography } from '@mui/material';
import { GLOBAL_USER } from '../../utils/constants';
import { Button, HeadsUpMessage } from '../../components';
import useConfirm from '../../hooks/useConfirm';
import { Vehicle } from '../../types';
import { updateOrder, OrderUpdate } from '../../services/order';
import useSnackbarNotifications from '../../hooks/useSnackbarNotifications';
import { LoadingDialogue } from '../../components/Spinners';
import { usePermissions } from '../../hooks/usePermissions';

interface Props {
  vehicle: Vehicle;
  isDisable: boolean;
  setTransactionErrors?: any;
  retryDecisionPending?: boolean;
}

const ActionMenu: React.FC<Props> = ({ vehicle, isDisable, setTransactionErrors, retryDecisionPending = false }) => {
  const { confirm } = useConfirm();
  const { hasPermissions } = usePermissions();
  const [open, setOpen] = React.useState(false);
  const [decisionPending, setDecisionPending] = useState(false);
  const anchorRef = React.useRef<HTMLButtonElement | null>(null);
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarNotifications();

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const VINCustomerNameGrid = (
    <Grid container>
      {vehicle.VIN && (
        <Grid item xs={6}>
          <Typography variant="body1" sx={{ color: (theme) => theme.palette.grey[600] }}>
            VIN
          </Typography>
          <Typography variant="body1">{vehicle.VIN || 'N/A'}</Typography>
        </Grid>
      )}
      <Grid item xs={6}>
        <Typography variant="body1" sx={{ color: (theme) => theme.palette.grey[600] }}>
          Customer Name
        </Typography>
        <Typography variant="body1">
          {vehicle.customerName || `${vehicle.First_Name__C} ${vehicle.Last_Name__C}`}
        </Typography>
      </Grid>
    </Grid>
  );

  const CancelOrderBody = (
    <Stack py={1} spacing={2}>
      {vehicle.VIN && (
        <Box px={1}>
          <HeadsUpMessage>Vehicle will be added to inventory once it is cancelled.</HeadsUpMessage>
        </Box>
      )}
      {VINCustomerNameGrid}
    </Stack>
  );

  const handleCancelOrder = async () => {
    const isConfirmed = await confirm({
      title: 'Cancel Order',
      description: `Are you sure you want to cancel this order? Please confirm that you would like to cancel the order.`,
      cancelButtonText: 'No, Keep this order',
      confirmButtonText: 'Yes, Cancel',
      confirmBody: CancelOrderBody,
    });
    if (isConfirmed) {
      const data = {
        actionType: 'CANCEL',
        orderNumber: vehicle?.orderId,
        vin: vehicle.VIN,
        requestor: GLOBAL_USER,
      };
      setDecisionPending(true);
      const res = await updateOrder(data as OrderUpdate);
      if (res.message === 'Success') {
        showSuccessSnackbar({ message: 'Success' });
      } else if (res?.sdfcResponse?.statusCode !== 200 || res?.vmsResponse?.statusCode !== 200) {
        setTransactionErrors(res);
      } else {
        showErrorSnackbar({ message: res.message });
      }
      setDecisionPending(false);
    }
  };

  const ReleaseToInventoryBody = (
    <Stack py={1} spacing={2}>
      {vehicle.VIN && (
        <Box px={1}>
          <HeadsUpMessage>Vehicle will be added to inventory once it is released.</HeadsUpMessage>
        </Box>
      )}
      {VINCustomerNameGrid}
    </Stack>
  );

  const handleReleaseToInventory = async () => {
    const isConfirmed = await confirm({
      title: 'Release Vehicle to inventory',
      description: `Please confirm if you would like to release the vehicle`,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Release Vehicle',
      confirmBody: ReleaseToInventoryBody,
    });
    if (isConfirmed) {
      const data = {
        actionType: 'RELEASE',
        orderNumber: vehicle?.orderId,
        vin: vehicle.VIN,
        requestor: GLOBAL_USER,
      };
      setDecisionPending(true);
      const res = await updateOrder(data as OrderUpdate);
      if (res.message === 'Success') {
        showSuccessSnackbar({ message: 'Success' });
      } else if (res?.sdfcResponse?.statusCode !== 200 || res?.vmsResponse?.statusCode !== 200) {
        setTransactionErrors(res);
      } else {
        showErrorSnackbar({ message: res.message });
      }
      setDecisionPending(false);
    }
  };

  return (
    <>
      <LoadingDialogue open={decisionPending || retryDecisionPending} />
      <Stack direction="row" spacing={2}>
        <div>
          <Button
            disabled={isDisable}
            ref={anchorRef}
            variant="outlined"
            id="composition-button"
            aria-controls={open ? 'composition-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleToggle}
            endIcon={<Icon icon="ri:arrow-drop-down-line" />}
          >
            Actions
          </Button>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            placement="bottom-start"
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList
                      autoFocusItem={open}
                      id="composition-menu"
                      aria-labelledby="composition-button"
                      onKeyDown={handleListKeyDown}
                    >
                      {hasPermissions(['PERFORM_CANCEL_ORDERS']) && vehicle?.currentStatus !== 'Cancelled' && (
                        <MenuItem onClick={handleCancelOrder}>
                          <Typography variant="body2">Cancel Order</Typography>
                        </MenuItem>
                      )}
                      {hasPermissions(['PERFORM_RELEASE_ORDERS_TO_INVENTORY']) &&
                        vehicle?.currentStatus !== 'Cancelled' &&
                        vehicle?.batchNumber && (
                          <MenuItem onClick={handleClose}>
                            <Typography variant="body2" onClick={handleReleaseToInventory}>
                              Release Vehicle to Inventory
                            </Typography>
                          </MenuItem>
                        )}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
      </Stack>
    </>
  );
};

export default ActionMenu;
